// libraries
import { ChangeEvent, ChangeEventHandler, useEffect, useRef, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Text,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useColorModeValue,
  useDisclosure,
  useToast,
  VStack,
  IconButton,
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Spinner,
  Center,
  Input,
  FormControl,
  FormLabel,
  FormErrorMessage,
} from '@chakra-ui/react';
import { MdCheck, MdClose } from 'react-icons/md';

// components
import { PageHeading, Panel } from '@bemap/ui';
import { useAuth, IUser, IChangePassword, base64 } from '@bemap/utils';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

// services
import { searchUser, updatePassword, updateUser, uploadProfilePhoto } from '../../services/profile-edit-service';

function ProfileEditPage() {
  const { user } = useAuth()
  const { t } = useTranslation()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const toast = useToast()
  const navigate = useNavigate()

  // consts
  const initialUserInfo: Partial<IUser> = {
    name: user?.name || '',
    email: '',
  };

  const initialPassword: IChangePassword = {
    old: '',
    new: '',
    confirm_new: '',
  };

  const errorMessages = {
    noError: '',
    empty: t('auth.forgot_password_error_empty'),
    mismatch: t('auth.forgot_password_error_mismatch'),
    tooSmall: t('auth.forgot_password_error_tooSmall'),
  }

  const isErrorInitial = {
    old: false,
    new: false,
    confirm_new: false,
  }

  // state
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [userInfo, setUserInfo] = useState<Partial<IUser>>(initialUserInfo);
  const [password, setPassword] = useState<IChangePassword>(initialPassword);
  const [isValid, setIsValid] = useState(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState('')
  const [isError, setIsError] = useState(isErrorInitial)
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [update, setUpdate] = useState<number | null>(null);

  useEffect(() => {
    if (user?.id) {
      loadInfo();
    }
    if (userInfo?.profilePhotoPath) {
      localStorage.removeItem('profile_photo')
      localStorage.setItem('profile_photo', userInfo?.profilePhotoPath)
    }
  }, [user, update]);

  //theme
  const background = useColorModeValue('transparent', 'transparent');
  const textColor = useColorModeValue('#1A202C', 'white');

  const loadInfo = async () => {
    if (!user?.id) return;

    setLoading(true)
    toast.closeAll()
    toast({
      id: 'carregando',
      title: t('general.loading'),
      status: 'loading',
      isClosable: true,
    })
    try {
      const response = await searchUser(user.id)
      toast.closeAll()
      setLoading(false)
      setUserInfo(response.message)
    } catch (error) {
      toast({
        title: t('general.error'),
        status: 'error',
        isClosable: true,
      })
    }
  }

  // api
  useEffect(() => {
    isValid && saveNewPassword();
  }, [isValid]);

  async function saveInfo() {
    if (!user?.id) return;

    toast({
      title: t('general.saving'),
      status: 'loading',
      isClosable: true,
    })
    setLoading(true)
    try {
      await updateUser({
        id: user.id,
        name: userInfo.name || '',
        email: userInfo.email || '',
        phone: userInfo.phone,
        profilePhotoPath: userInfo.profilePhotoPath,
      });
      setLoading(false)
      toast.closeAll()
      toast({
        title: t('general.success'),
        status: 'success',
        isClosable: true,
      })
    } catch (error) {
      toast.closeAll()
      setLoading(false)
      toast({
        title: t('general.error'),
        status: 'error',
        isClosable: true,
      })
    }
  }

  async function saveNewPassword() {
    if (!user?.id) return;

    try {
      await updatePassword(user.id, {
        old: password.old,
        new: password.new
      })
      toast({
        title: t('general.success'),
        status: 'success',
        isClosable: true,
      })
      onClose()
    } catch (error: any) {
      setIsValid(false)
      setError(error.message)
    }
  }

  const hasAnyEmptyValues = password.old === '' || password.new === '' || password.confirm_new === '' ? true : false

  async function checkPasswords() {
    const newPasswordsAreEqual = password.new === password.confirm_new;
    const smallpass =
      (password.new?.length ?? 0) < 6 || (password.confirm_new?.length ?? 0) < 6;

    if (!password.old) {
      setIsError({
        ...isError,
        ['old']: true
      })
      setError(errorMessages.empty)
    }

    if (smallpass) {
      setIsError({
        ...isError,
        ['new']: true,
        ['confirm_new']: true
      })
      setError(errorMessages.tooSmall)
    }

    if (!newPasswordsAreEqual) {
      setIsError({
        ...isError,
        ['new']: true,
        ['confirm_new']: true
      })
      setError(errorMessages.mismatch)
    }

    if (!hasAnyEmptyValues && newPasswordsAreEqual) {
      setIsError({
        ...isError,
        ['old']: false,
        ['new']: false,
        ['confirm_new']: false
      })
      setIsValid(true)
      setError(errorMessages.noError)
    } else {
      setIsValid(false)
    }
  }

  function handleChange(event: any) {
    setUserInfo({
      ...userInfo,
      [event.target.name]: event.target.value,
    });
  }

  const handlePasswordChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    setPassword({
      ...password,
      [event.target.name]: event.target.value,
    })
  }

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      const fileReader = new FileReader();
      fileReader.onload = () => {
        setPreviewUrl(fileReader.result as string);
      };
      fileReader.readAsDataURL(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !user?.currentTeamId) {
      toast({
        title: t('general.error'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const formData = new FormData();
    formData.append('image', selectedFile);

    try {
      await uploadProfilePhoto(user.currentTeamId, formData);

      setUpdate(Date.now())
      handleCancel()
      toast({
        title: t('general.success'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Oops!',
        description: t('general.error'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleCancel = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (loading) {
    return (
      <Center h="400px">
        <Spinner size="xl" />
      </Center>
    );
  }

  // main
  return (
    <Flex direction="column" gap={4}>
      <PageHeading title={t('account.page_title')} actions={[
        {
          label: t('actions.back'),
          onClick: () => navigate(-1),
          variant: 'outline',
          colorScheme: 'gray',
        },
        {
          label: t('account.password_change_button'),
          onClick: () => onOpen(),
          variant: 'outline',
          colorScheme: 'blue',
        },
        {
          label: t('actions.save'),
          onClick: () => saveInfo(),
          variant: 'solid',
          colorScheme: 'blue',
        }
      ]} />

      <Flex direction={{ base: 'column', lg: 'row' }} gap={8} width='100%'>
        <VStack gap={4} alignItems='flex-start' w={'100%'} maxW={'500px'}>
          <Text fontSize='22px' fontWeight='700' lineHeight='100%' color={textColor}>{t("account.profile_photo")}</Text>
          <Panel padding='25px'>
            <VStack spacing={4} align="center">
              <Box position="relative">
                <Avatar
                  width={'200px'}
                  height={'200px'}
                  src={previewUrl || base64(userInfo?.profilePhotoPath || '') || ''}
                  cursor="pointer"
                />
                <Box
                  onClick={() => fileInputRef.current?.click()}
                  cursor={'pointer'}
                  position="absolute"
                  top="0"
                  left="0"
                  right="0"
                  bottom="0"
                  opacity="0"
                  bg="blackAlpha.600"
                  color="white"
                  display="flex"
                  padding={'20px'}
                  textAlign={'center'}
                  alignItems="center"
                  justifyContent="center"
                  borderRadius="full"
                  transition="opacity 0.2s"
                  _hover={{ opacity: 1 }}
                >
                  <Text fontSize="sm">{t("account.profile_photo_description")}</Text>
                </Box>
              </Box>
              <input
                type="file"
                hidden
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/*"
              />
              {selectedFile && (
                <Flex gap={6}>
                  <IconButton borderRadius={'50%'} aria-label='Cancelar' colorScheme="red" size="sm" onClick={handleCancel}>
                    <MdClose />
                  </IconButton>
                  <IconButton borderRadius={'50%'} aria-label='Salvar' colorScheme="green" size="sm" onClick={handleUpload}>
                    <MdCheck />
                  </IconButton>
                </Flex>
              )}
            </VStack>
          </Panel>
        </VStack>

        <VStack gap={4} alignItems='flex-start' w={'100%'}>
          <Text fontSize='22px' fontWeight='700' lineHeight='100%' color={textColor}>{t('account.personal_data')}</Text>
          <Panel padding='25px'>
            <Flex direction={'column'} gap={'20px'}>
              <FormControl>
                <FormLabel color={textColor}>{t('account.fullname')}</FormLabel>
                <Input
                  color={textColor}
                  bg={background}
                  name="name"
                  placeholder={t("general.placeholder_name")}
                  type="text"
                  onChange={handleChange}
                  value={userInfo.name || ''}
                />
              </FormControl>
              <FormControl>
                <FormLabel color={textColor}>E-mail</FormLabel>
                <Input
                  color={textColor}
                  bg={background}
                  name="email"
                  placeholder={t("general.placeholder_email")}
                  type="email"
                  onChange={handleChange}
                  value={userInfo.email || ''}
                />
              </FormControl>
              <FormControl>
                <FormLabel color={textColor}>{t('account.phone')}</FormLabel>
                <Input
                  color={textColor}
                  bg={background}
                  name="phone"
                  placeholder={t('general.placeholder_phone')}
                  type="text"
                  onChange={handleChange}
                  value={userInfo.phone || ''}
                />
              </FormControl>
            </Flex>
          </Panel>
        </VStack>
      </Flex>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('profile.define_paswword')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack gap="30px" alignItems="center">
              {error && (
                <Alert status='error'>
                  <AlertIcon />
                  <Flex direction={'column'}>
                    <AlertTitle>{t('auth.forgot_password_step_three_alert')}</AlertTitle>
                    <AlertDescription>{error}</AlertDescription>
                  </Flex>
                </Alert>
              )}
              <FormControl isInvalid={isError.old}>
                <FormLabel color={textColor}>{t("profile.current_password")}</FormLabel>
                <Input
                  color={textColor}
                  bg={background}
                  type="password"
                  value={password.old}
                  onChange={handlePasswordChange}
                  name='old'
                />
                {isError.old && error && <FormErrorMessage>{error}</FormErrorMessage>}
              </FormControl>
              <FormControl isInvalid={isError.new}>
                <FormLabel color={textColor}>{t("profile.new_password")}</FormLabel>
                <Input
                  color={textColor}
                  bg={background}
                  type="password"
                  value={password.new}
                  onChange={handlePasswordChange}
                  name="new"
                />
                {isError.new && error && <FormErrorMessage>{error}</FormErrorMessage>}
              </FormControl>
              <FormControl isInvalid={isError.confirm_new}>
                <FormLabel color={textColor}>{t("profile.confirm_password")}</FormLabel>
                <Input
                  color={textColor}
                  bg={background}
                  type="password"
                  value={password.confirm_new}
                  onChange={handlePasswordChange}
                  name='confirm_new'
                />
                {isError.confirm_new && error && <FormErrorMessage>{error}</FormErrorMessage>}
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant='ghost' onClick={onClose}>{t('actions.cancel')}</Button>
            <Button
              disabled={hasAnyEmptyValues}
              onClick={checkPasswords}
              colorScheme={'blue'}
            >
              {t('profile.change_password')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  )
}

export default ProfileEditPage; 